/**
 * Simple in-memory request cache for GET requests
 * Prevents redundant API calls within a short time window
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes default

  /**
   * Get cached data if available and not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.expiresAt) {
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Get stale (expired) cache entry without removing it
   */
  getStale<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() <= entry.expiresAt) {
      return null;
    }

    return entry.data as T;
  }

  /**
   * Store data in cache with optional TTL
   */
  set<T>(key: string, data: T, ttlMs?: number): void {
    const now = Date.now();
    const ttl = ttlMs ?? this.defaultTTL;
    
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  /**
   * Invalidate cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }
}

// Singleton instance
export const requestCache = new RequestCache();

// In-flight request deduplication — concurrent callers share one fetch
const inFlight = new Map<string, Promise<any>>();

/**
 * Generate cache key from request path and options
 */
export function getCacheKey(path: string, options?: RequestInit): string {
  const method = (options?.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    // Only cache GET requests
    return '';
  }
  
  // Include query params in cache key
  const url = path.startsWith('http') ? new URL(path) : new URL(path, 'http://dummy');
  const query = url.searchParams.toString();
  return `${method}:${url.pathname}${query ? `?${query}` : ''}`;
}

/**
 * Cache-aware fetch wrapper
 */
export async function cachedFetch<T>(
  path: string,
  fetchFn: () => Promise<T>,
  options?: {
    ttlMs?: number;
    cacheKey?: string;
    swr?: boolean;
    /**
     * Surfaced when a stale-while-revalidate BACKGROUND refresh fails. The
     * stale value is still served to the caller (SWR contract), but the
     * failure is FAIL-LOUD (FR-FE-CODEGEN-FAIL-LOUD-DEFENSIVE-READ-STD): it is
     * reported here instead of being swallowed, and the stale entry is
     * invalidated so the next call performs a real (throwing) fetch rather
     * than re-serving stale data forever.
     */
    onSwrError?: (error: unknown) => void;
  }
): Promise<T> {
  const cacheKey = options?.cacheKey || getCacheKey(path);
  
  // Only cache GET requests
  if (!cacheKey) {
    return fetchFn();
  }
  
  // Check cache first
  const cached = requestCache.get<T>(cacheKey);
  if (cached !== null) {
    return cached;
  }

  // Deduplicate in-flight requests
  const pending = inFlight.get(cacheKey);
  if (pending) {
    return pending as Promise<T>;
  }

  // If SWR is enabled, try to serve stale data while refreshing in background
  if (options?.swr) {
    const stale = requestCache.getStale<T>(cacheKey);
    if (stale !== null) {
      // Refresh in background without blocking the caller
      void (async () => {
        try {
          const freshData = await fetchFn();
          requestCache.set(cacheKey, freshData, options.ttlMs);
        } catch (error) {
          // FAIL-LOUD: a failed background refresh must NOT be silently
          // swallowed and the stale entry must NOT be re-served forever
          // (that would mask a persistently-failing endpoint). Invalidate the
          // stale entry so the next call does a real (throwing) fetch, and
          // surface the failure to the caller-supplied handler.
          requestCache.invalidate(cacheKey);
          if (options.onSwrError) {
            options.onSwrError(error);
          } else {
            console.warn('[RequestCache] SWR refresh failed', { cacheKey, error });
          }
        }
      })();

      return stale;
    }
  }
  
  // Fetch, cache, and deduplicate
  const promise = fetchFn().then(data => {
    requestCache.set(cacheKey, data, options?.ttlMs);
    inFlight.delete(cacheKey);
    return data;
  }).catch(err => {
    inFlight.delete(cacheKey);
    throw err;
  });
  inFlight.set(cacheKey, promise);
  return promise;
}

export function getStale<T>(key: string): T | null {
  return requestCache.getStale<T>(key);
}


